/**
 * FinTasksContext - Centralized Task State Management
 * ====================================================
 * FinTasks 2.0 - Phase 1 + Phase 2
 * 
 * This context provides a SINGLE SOURCE OF TRUTH for tasks in FinList.
 * It wraps the existing useTasks hook functionality and adds:
 * - Centralized state accessible from any component
 * - Safe Firestore operations with data sanitization
 * - Task category filtering (financial vs personal)
 * - Phase 2: Smart task generation from financial events
 * - Phase 2: Auto-complete tasks when related actions are taken
 * 
 * SAFETY RULES:
 * - Does NOT modify existing finTasksService.js functions
 * - Does NOT change Firestore schema
 * - Components can still use useTasks hook directly if needed
 * - All new logic is additive and isolated
 * 
 * @version 2.0.0 - Phase 2
 * @date 2024-12-04
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTodoLists,
  getAllTasks,
  getFilteredTasks,
  getTasksByList,
  createTask,
  updateTask,
  deleteTask,
  toggleTaskStatus,
  archiveTask,
  unarchiveTask,
  createTodoList,
  TASK_STATUS,
  TASK_PRIORITY
} from '@/services/finTasksService';
import { sanitizeTaskData, buildPath } from '@/services/firestoreHelpers';
// Phase 2: Smart tasks
import {
  generateAllSmartTasks,
  hasDuplicateTask,
  SMART_TASK_TYPE,
  SMART_TASK_TRIGGER
} from '@/services/smartTasksService';
// Phase 2: Auto-complete
import {
  processFinancialEvent,
  executeAutoComplete,
  FINANCIAL_EVENT
} from '@/services/taskAutoCompleteService';

// ============================================================================
// CONSTANTS
// ============================================================================

export const TASK_CATEGORY = {
  ALL: 'all',
  FINANCIAL: 'financial',
  PERSONAL: 'personal'
};

export const FILTER_TYPES = {
  ALL: 'all',
  TODAY: 'today',
  UPCOMING: 'upcoming',
  OVERDUE: 'overdue',
  COMPLETED: 'completed',
  FINANCIAL: 'financial'
};

// Re-export for convenience
export { SMART_TASK_TYPE, SMART_TASK_TRIGGER, FINANCIAL_EVENT };

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const FinTasksContext = createContext(null);

/**
 * Hook to access FinTasks context
 * 
 * @returns {Object} Context value with tasks, lists, and operations
 * @throws {Error} If used outside of FinTasksProvider
 * 
 * @example
 * const { tasks, addTask, completeTask } = useFinTasks();
 */
export function useFinTasks() {
  const context = useContext(FinTasksContext);
  if (!context) {
    throw new Error('useFinTasks must be used within a FinTasksProvider');
  }
  return context;
}

/**
 * Safe version that returns null if outside provider
 * Useful for optional context usage
 */
export function useFinTasksSafe() {
  return useContext(FinTasksContext);
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

/**
 * FinTasksProvider
 * Provides centralized task state management for the entire app
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export function FinTasksProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  // ============================================================================
  // STATE
  // ============================================================================
  
  // Core data
  const [tasks, setTasks] = useState([]);
  const [lists, setLists] = useState([]);
  const [defaultListId, setDefaultListId] = useState(null);
  
  // Loading & error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [activeFilter, setActiveFilter] = useState(FILTER_TYPES.ALL);
  const [taskCategory, setTaskCategory] = useState(TASK_CATEGORY.ALL);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Tasks filtered by category (financial/personal)
   */
  const filteredByCategory = useMemo(() => {
    if (taskCategory === TASK_CATEGORY.ALL) {
      return tasks;
    }
    
    if (taskCategory === TASK_CATEGORY.FINANCIAL) {
      return tasks.filter(t => t.isFinancialTask === true);
    }
    
    if (taskCategory === TASK_CATEGORY.PERSONAL) {
      return tasks.filter(t => t.isFinancialTask !== true);
    }
    
    return tasks;
  }, [tasks, taskCategory]);

  /**
   * Task statistics
   */
  const stats = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === TASK_STATUS.DONE).length;
    const pending = tasks.filter(t => t.status === TASK_STATUS.TODO || t.status === TASK_STATUS.IN_PROGRESS).length;
    const financial = tasks.filter(t => t.isFinancialTask).length;
    const personal = tasks.filter(t => !t.isFinancialTask).length;
    
    const overdue = tasks.filter(t => {
      if (!t.dueDate || t.status === TASK_STATUS.DONE) return false;
      const dueDate = new Date(t.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < now;
    }).length;
    
    const dueToday = tasks.filter(t => {
      if (!t.dueDate || t.status === TASK_STATUS.DONE) return false;
      const dueDate = new Date(t.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() === now.getTime();
    }).length;

    const financialAmount = tasks
      .filter(t => t.isFinancialTask && t.estimatedAmount > 0 && t.status !== TASK_STATUS.DONE)
      .reduce((sum, t) => sum + (parseFloat(t.estimatedAmount) || 0), 0);

    return {
      total,
      completed,
      pending,
      financial,
      personal,
      overdue,
      dueToday,
      financialAmount,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [tasks]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  /**
   * Fetch all tasks and lists for the user
   */
  const fetchData = useCallback(async () => {
    if (!user?.uid) {
      setTasks([]);
      setLists([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch lists first
      const fetchedLists = await getTodoLists(user.uid);
      setLists(fetchedLists);

      // Set default list
      const defaultList = fetchedLists.find(l => l.isDefault) || fetchedLists[0];
      if (defaultList) {
        setDefaultListId(defaultList.id);
      }

      // Fetch tasks based on active filter
      let fetchedTasks;
      if (activeFilter === FILTER_TYPES.ALL) {
        fetchedTasks = await getAllTasks(user.uid);
      } else {
        fetchedTasks = await getFilteredTasks(user.uid, activeFilter);
      }

      setTasks(fetchedTasks);
    } catch (err) {
      console.error('[FinTasksContext] Error fetching data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid, activeFilter]);

  /**
   * Initial data load
   */
  useEffect(() => {
    if (authLoading) return;
    fetchData();
  }, [authLoading, fetchData]);

  /**
   * Ensure default list exists
   */
  useEffect(() => {
    const ensureDefaultList = async () => {
      if (!user?.uid || lists.length > 0 || isLoading) return;

      try {
        console.log('[FinTasksContext] Creating default list...');
        const newList = await createTodoList(user.uid, {
          title: 'My Tasks',
          isDefault: true
        });
        setLists([newList]);
        setDefaultListId(newList.id);
      } catch (err) {
        console.error('[FinTasksContext] Error creating default list:', err);
      }
    };

    ensureDefaultList();
  }, [user?.uid, lists.length, isLoading]);

  // ============================================================================
  // TASK OPERATIONS
  // ============================================================================

  /**
   * Add a new task with sanitized data
   * 
   * @param {Object} taskData - Task data to create
   * @returns {Promise<Object>} - Created task
   */
  const addTask = useCallback(async (taskData) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    const listId = taskData.listId || defaultListId;
    if (!listId) {
      throw new Error('No list ID provided and no default list available');
    }

    try {
      // Sanitize task data before sending to Firestore
      const sanitizedData = sanitizeTaskData({
        ...taskData,
        listId
      });

      console.log('[FinTasksContext] Creating task:', sanitizedData);
      
      const newTask = await createTask(user.uid, listId, sanitizedData);
      
      // Add to local state
      setTasks(prev => [newTask, ...prev]);
      
      return newTask;
    } catch (err) {
      console.error('[FinTasksContext] Error adding task:', err);
      throw err;
    }
  }, [user?.uid, defaultListId]);

  /**
   * Update an existing task
   * 
   * @param {string} listId - List ID containing the task
   * @param {string} taskId - Task ID to update
   * @param {Object} updates - Fields to update
   * @returns {Promise<boolean>}
   */
  const editTask = useCallback(async (listId, taskId, updates) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      // Sanitize updates
      const sanitizedUpdates = sanitizeTaskData(updates);
      
      await updateTask(user.uid, listId, taskId, sanitizedUpdates);
      
      // Update local state
      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, ...sanitizedUpdates } : task
      ));
      
      return true;
    } catch (err) {
      console.error('[FinTasksContext] Error updating task:', err);
      throw err;
    }
  }, [user?.uid]);

  /**
   * Complete a task (toggle status)
   * 
   * @param {Object} task - Task to complete
   * @returns {Promise<string>} - New status
   */
  const completeTask = useCallback(async (task) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      const newStatus = await toggleTaskStatus(user.uid, task.listId, task.id);
      
      // Update local state
      setTasks(prev => prev.map(t =>
        t.id === task.id 
          ? { 
              ...t, 
              status: newStatus,
              completedAt: newStatus === TASK_STATUS.DONE ? new Date().toISOString() : null
            } 
          : t
      ));
      
      return newStatus;
    } catch (err) {
      console.error('[FinTasksContext] Error completing task:', err);
      throw err;
    }
  }, [user?.uid]);

  /**
   * Delete a task
   * 
   * @param {string} listId - List ID containing the task
   * @param {string} taskId - Task ID to delete
   * @returns {Promise<boolean>}
   */
  const removeTask = useCallback(async (listId, taskId) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      await deleteTask(user.uid, listId, taskId);
      
      // Remove from local state
      setTasks(prev => prev.filter(t => t.id !== taskId));
      
      return true;
    } catch (err) {
      console.error('[FinTasksContext] Error deleting task:', err);
      throw err;
    }
  }, [user?.uid]);

  /**
   * Archive a task
   * 
   * @param {string} listId - List ID
   * @param {string} taskId - Task ID
   * @returns {Promise<boolean>}
   */
  const archiveTaskItem = useCallback(async (listId, taskId) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      await archiveTask(user.uid, listId, taskId);
      
      setTasks(prev => prev.map(t =>
        t.id === taskId 
          ? { ...t, status: TASK_STATUS.ARCHIVED, archivedAt: new Date().toISOString() }
          : t
      ));
      
      return true;
    } catch (err) {
      console.error('[FinTasksContext] Error archiving task:', err);
      throw err;
    }
  }, [user?.uid]);

  /**
   * Unarchive a task
   * 
   * @param {string} listId - List ID
   * @param {string} taskId - Task ID
   * @returns {Promise<boolean>}
   */
  const unarchiveTaskItem = useCallback(async (listId, taskId) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      await unarchiveTask(user.uid, listId, taskId);
      
      // Refetch to get correct status
      await fetchData();
      
      return true;
    } catch (err) {
      console.error('[FinTasksContext] Error unarchiving task:', err);
      throw err;
    }
  }, [user?.uid, fetchData]);

  // ============================================================================
  // LIST OPERATIONS
  // ============================================================================

  /**
   * Create a new task list
   * 
   * @param {Object} listData - List data
   * @returns {Promise<Object>} - Created list
   */
  const addList = useCallback(async (listData) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      const newList = await createTodoList(user.uid, listData);
      setLists(prev => [...prev, newList]);
      return newList;
    } catch (err) {
      console.error('[FinTasksContext] Error creating list:', err);
      throw err;
    }
  }, [user?.uid]);

  // ============================================================================
  // FILTER OPERATIONS
  // ============================================================================

  /**
   * Change the active filter and refetch tasks
   * 
   * @param {string} filter - New filter value
   */
  const changeFilter = useCallback((filter) => {
    setActiveFilter(filter);
  }, []);

  /**
   * Change the task category filter (financial/personal/all)
   * 
   * @param {string} category - New category value
   */
  const changeTaskCategory = useCallback((category) => {
    setTaskCategory(category);
  }, []);

  // ============================================================================
  // PHASE 2: SMART TASK GENERATION
  // ============================================================================

  /**
   * Generate smart tasks from current financial state
   * This analyzes budgets, goals, and bills to suggest tasks
   * 
   * @param {Object} financialState - Current financial data
   * @param {Object} financialState.budgetSummary - Budget state from budgetService
   * @param {Array} financialState.upcomingBills - Bills due soon
   * @param {Array} financialState.goals - Savings goals
   * @returns {Promise<Array>} Created smart tasks
   */
  const generateSmartTasks = useCallback(async (financialState = {}) => {
    if (!user?.uid || !defaultListId) {
      console.warn('[FinTasksContext] Cannot generate smart tasks: user or list not ready');
      return [];
    }

    try {
      const { budgetSummary, upcomingBills = [], goals = [] } = financialState;

      // Generate suggestions (deduplication included)
      const suggestions = generateAllSmartTasks({
        budgetSummary,
        upcomingBills,
        goals,
        existingTasks: tasks,
        currency: user.currency || 'USD'
      });

      if (suggestions.length === 0) {
        console.log('[FinTasksContext] No new smart tasks to create');
        return [];
      }

      console.log(`[FinTasksContext] Creating ${suggestions.length} smart tasks`);

      // Create each suggested task
      const createdTasks = [];
      for (const suggestion of suggestions) {
        try {
          const newTask = await addTask({
            ...suggestion,
            listId: defaultListId
          });
          createdTasks.push(newTask);
        } catch (err) {
          console.error('[FinTasksContext] Error creating smart task:', err);
        }
      }

      return createdTasks;
    } catch (err) {
      console.error('[FinTasksContext] Error generating smart tasks:', err);
      return [];
    }
  }, [user?.uid, user?.currency, defaultListId, tasks, addTask]);

  // ============================================================================
  // PHASE 2: AUTO-COMPLETE ON FINANCIAL EVENTS
  // ============================================================================

  /**
   * Process a financial event and auto-complete related tasks
   * Call this when the user performs a financial action (logs income, pays bill, etc.)
   * 
   * @param {string} eventType - Event type from FINANCIAL_EVENT
   * @param {Object} eventData - Event-specific data
   * @returns {Promise<Object>} Results of auto-completion
   */
  const handleFinancialEvent = useCallback(async (eventType, eventData) => {
    if (!user?.uid) {
      return { completed: [], failed: [], skipped: [] };
    }

    try {
      // Find tasks that should be auto-completed
      const tasksToComplete = processFinancialEvent(eventType, eventData, tasks);

      if (tasksToComplete.length === 0) {
        return { completed: [], failed: [], skipped: [] };
      }

      console.log(`[FinTasksContext] Auto-completing ${tasksToComplete.length} tasks for ${eventType}`);

      // Execute auto-completion
      const results = await executeAutoComplete(tasksToComplete, completeTask);

      // Refresh tasks if any were completed
      if (results.completed.length > 0) {
        await fetchData();
      }

      return results;
    } catch (err) {
      console.error('[FinTasksContext] Error handling financial event:', err);
      return { completed: [], failed: [], skipped: [] };
    }
  }, [user?.uid, tasks, completeTask, fetchData]);

  /**
   * Convenience method for transaction events
   */
  const onTransactionCreated = useCallback((transaction) => {
    const eventType = transaction.type === 'income' 
      ? FINANCIAL_EVENT.INCOME_RECEIVED 
      : FINANCIAL_EVENT.TRANSACTION_CREATED;
    return handleFinancialEvent(eventType, transaction);
  }, [handleFinancialEvent]);

  /**
   * Convenience method for goal contribution events
   */
  const onGoalContribution = useCallback((contribution) => {
    return handleFinancialEvent(FINANCIAL_EVENT.GOAL_CONTRIBUTION, contribution);
  }, [handleFinancialEvent]);

  /**
   * Convenience method for budget update events
   */
  const onBudgetUpdated = useCallback((budgetUpdate) => {
    return handleFinancialEvent(FINANCIAL_EVENT.BUDGET_UPDATED, budgetUpdate);
  }, [handleFinancialEvent]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const contextValue = useMemo(() => ({
    // Data
    tasks: filteredByCategory,
    allTasks: tasks,
    lists,
    defaultListId,
    stats,
    
    // State
    isLoading,
    error,
    activeFilter,
    taskCategory,
    
    // Task operations
    addTask,
    editTask,
    completeTask,
    removeTask,
    archiveTaskItem,
    unarchiveTaskItem,
    
    // List operations
    addList,
    
    // Filter operations
    changeFilter,
    changeTaskCategory,
    
    // Phase 2: Smart tasks
    generateSmartTasks,
    
    // Phase 2: Auto-complete
    handleFinancialEvent,
    onTransactionCreated,
    onGoalContribution,
    onBudgetUpdated,
    
    // Utilities
    refetch: fetchData,
    
    // Re-export constants for convenience
    TASK_STATUS,
    TASK_PRIORITY,
    TASK_CATEGORY,
    FILTER_TYPES,
    SMART_TASK_TYPE,
    SMART_TASK_TRIGGER,
    FINANCIAL_EVENT
  }), [
    filteredByCategory,
    tasks,
    lists,
    defaultListId,
    stats,
    isLoading,
    error,
    activeFilter,
    taskCategory,
    addTask,
    editTask,
    completeTask,
    removeTask,
    archiveTaskItem,
    unarchiveTaskItem,
    addList,
    changeFilter,
    changeTaskCategory,
    generateSmartTasks,
    handleFinancialEvent,
    onTransactionCreated,
    onGoalContribution,
    onBudgetUpdated,
    fetchData
  ]);

  return (
    <FinTasksContext.Provider value={contextValue}>
      {children}
    </FinTasksContext.Provider>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export default FinTasksContext;
export { TASK_STATUS, TASK_PRIORITY };
